import { useLazyQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { Icon, IconMap } from '@flixbus/honeycomb-icons-react';
import { Autocomplete } from '@flixbus/honeycomb-react';

import { SEACH_CITIES_WITH_STATIONS_ONLY, SEARCH_CITY_BY_NAME } from './qql/locationSearch';

const DATA_ROUTE = {
	all: 'searchCitiesByName',
	onlyWithStations: 'searchCitiesWithStationsByName',
};

const LocationSearchV2 = ({
	value = [],
	onSelection = () => {},
	onReset = () => {},
	onSingleItemReset = () => {},
	label = 'Location',
	placeholder = 'e.g. Boston',
	extraClasses = '',
	readOnly = false,
	searchOnlyWithStations = false,
	disabled = false,
	withIcon = true,
}) => {
	const [inputValue, setInputValue] = useState('');

	const [getCitiesByName, { loading }] = useLazyQuery(
		searchOnlyWithStations ? SEACH_CITIES_WITH_STATIONS_ONLY : SEARCH_CITY_BY_NAME
	);

	const selectedOptions = useMemo(() => {
		return value.map((item) => ({
			title: item.name,
			value: item.id,
		}));
	}, [value]);

	const handleInputChange = (event) => {
		const nextValue = event?.target?.value ?? '';
		setInputValue(nextValue);
	};

	const handleDebounce = async (typedValue) => {
		if (!typedValue) return [];

		try {
			const { data } = await getCitiesByName({
				variables: { cityName: typedValue },
			});

			const route = searchOnlyWithStations ? DATA_ROUTE.onlyWithStations : DATA_ROUTE.all;
			const dataToMap = data?.[route] ?? [];

			return dataToMap.map((city) => ({
				title: city.name,
				value: city.id,
			}));
		} catch {
			return [];
		}
	};

	const handleAutocompleteChange = (selectedOptions) => {
		const nextSelected = Array.isArray(selectedOptions) ? selectedOptions : [];

		const previousSelectedIds = value.map((item) => item.id);
		const nextSelectedIds = nextSelected.map((item) => item.value);

		if (nextSelectedIds.length > previousSelectedIds.length) {
			const addedOption = nextSelected.find((item) => !previousSelectedIds.includes(item.value));

			if (addedOption) {
				onSelection({
					id: addedOption.value,
					name: addedOption.title,
				});
				setInputValue('');
			}

			return;
		}

		if (nextSelectedIds.length < previousSelectedIds.length) {
			if (nextSelectedIds.length === 0) {
				onReset();
				return;
			}

			const removedItem = value.find((item) => !nextSelectedIds.includes(item.id));

			if (removedItem) {
				onSingleItemReset(removedItem);
			}
		}
	};

	return (
		<Autocomplete
			multiple
			value={selectedOptions}
			inputValue={inputValue}
			options={[]}
			debounce={300}
			onDebounce={handleDebounce}
			onChange={handleAutocompleteChange}
			extraClasses={extraClasses}
			comboboxProps={{
				loading,
				label,
				placeholder,
				iconLeft: withIcon ? <Icon InlineIcon={IconMap} /> : null,
				onChange: handleInputChange,
				disabled,
				readOnly,
				limitSelectedOptions: 3,
				tagProps: (selectedOption) => ({
					closeProps: {
						label: `Remove ${selectedOption.title}`,
					},
				}),
			}}
			listBoxProps={{
				label,
				optionsToDisplay: 5,
			}}
		/>
	);
};

export default LocationSearchV2;
